import React from 'react';
import { 
  FaShieldAlt, 
  FaLock, 
  FaMobileAlt, 
  FaCheckCircle, 
  FaClock,
  FaExclamationTriangle,
  FaInfoCircle
} from 'react-icons/fa';

const VerificationBanner = ({ 
  step = 1, 
  totalSteps = 2, 
  title = "Two-Step Verification",
  subtitle = "Secure your account with phone verification",
  currentStep = "Add Phone Number",
  nextStep = "Verify Code",
  phoneNumber = "",
  codeExpiry = null,
  showTimer = false,
  className = ""
}) => {
  const getStepIcon = () => {
    switch (step) {
      case 1:
        return <FaMobileAlt className="text-2xl" />;
      case 2:
        return <FaCheckCircle className="text-2xl" />;
      default:
        return <FaShieldAlt className="text-2xl" />;
    }
  };

  const getStepColor = () => {
    switch (step) {
      case 1:
        return "from-blue-500 to-blue-600";
      case 2:
        return "from-green-500 to-green-600";
      default:
        return "from-orange-500 to-orange-600";
    }
  };

  const formatTimeLeft = () => {
    if (!codeExpiry) return "";
    const seconds = Math.max(0, Math.floor((codeExpiry - Date.now()) / 1000));
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <div className={`text-center mb-8 ${className}`}>
      {/* Main Icon */}
      <div className="relative mb-6">
        <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${getStepColor()} rounded-full mb-4 shadow-lg transform hover:scale-105 transition-all duration-300`}>
          {getStepIcon()}
        </div>
        
        {/* Animated Ring */}
        <div className="absolute inset-0 w-20 h-20 rounded-full border-4 border-orange-300 opacity-30 animate-ping" />
        <div className="absolute inset-0 w-20 h-20 rounded-full border-2 border-orange-200 opacity-50 animate-pulse" />
      </div>

      {/* Title */}
      <h2 className="text-3xl md:text-4xl font-bold text-navy-700 dark:text-white mb-3">
        {title}
      </h2>

      {/* Subtitle */}
      <p className="text-base text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto leading-relaxed">
        {subtitle}
      </p>

      {/* Enhanced Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-center space-x-3 mb-3">
          {Array.from({ length: totalSteps }, (_, index) => (
            <div key={index} className="relative">
              <div className={`w-4 h-4 rounded-full transition-all duration-300 ${
                index + 1 < step 
                  ? 'bg-green-500' 
                  : index + 1 === step 
                  ? 'bg-orange-500 animate-pulse' 
                  : 'bg-gray-300'
              }`} />
              
              {/* Connection Line */}
              {index < totalSteps - 1 && (
                <div className={`absolute top-2 left-4 w-8 h-0.5 transition-all duration-300 ${
                  index + 1 < step ? 'bg-green-500' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
        
        {/* Step Labels */}
        <div className="flex items-center justify-center space-x-8">
          <div className={`text-sm font-medium transition-all duration-300 ${
            step >= 1 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'
          }`}>
            <div className="flex items-center gap-2">
              <FaMobileAlt className="h-4 w-4" />
              <span>Step 1: {currentStep}</span>
            </div>
          </div>
          
          <div className={`text-sm font-medium transition-all duration-300 ${
            step >= 2 ? 'text-green-600 dark:text-green-400' : 'text-gray-400'
          }`}>
            <div className="flex items-center gap-2">
              <FaCheckCircle className="h-4 w-4" />
              <span>Step 2: {nextStep}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Content Based on Step */}
      {step === 1 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <FaInfoCircle className="text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-left">
              <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                Why Phone Verification?
              </p>
              <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-1">
                <li>• Enhanced account security</li>
                <li>• Quick password recovery</li>
                <li>• Important notifications</li>
                <li>• Two-factor authentication</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {step === 2 && phoneNumber && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FaMobileAlt className="text-green-500" />
              <div className="text-left">
                <p className="text-sm font-medium text-green-700 dark:text-green-300">
                  Code sent to:
                </p>
                <p className="text-sm text-green-600 dark:text-green-400 font-mono">
                  {phoneNumber}
                </p>
              </div>
            </div>
            
            {showTimer && codeExpiry && (
              <div className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400">
                <FaClock className="animate-pulse" />
                <span className="font-mono font-semibold">{formatTimeLeft()}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Security Badge */}
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-navy-800 rounded-full text-sm text-gray-600 dark:text-gray-400">
        <FaLock className="h-4 w-4 text-orange-500" />
        <span>256-bit encryption</span>
      </div>
    </div>
  );
};

export default VerificationBanner; 